import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtNum, fmtDate, today } from "@/lib/format";
import { Plus, Trash2, CheckCircle2, XCircle, Send, Zap, Award, Boxes, ArrowRight } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";
import ApprovalInboxCard from "@/components/ApprovalInboxCard";

// -------- Contracts --------
function ContractsTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { vendor_id: "", item_id: "", contract_price: 0, currency: "INR",
                   moq: 0, lead_time_days: 0, effective_from: today(),
                   effective_to: "", payment_terms: "Net 30", discount_slabs: [], is_active: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setRows((await api.get("/procurement/contracts")).data);
    setItems((await api.get("/items")).data);
    setVendors((await api.get("/parties?type=vendor")).data.filter?.((p) => p.party_type !== "customer") || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.vendor_id || !form.item_id || !form.contract_price) return toast.error("Vendor, item and price required");
    try { await api.post("/procurement/contracts", { ...form, effective_to: form.effective_to || null }); toast.success("Saved"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete contract?")) return; await api.delete(`/procurement/contracts/${id}`); load(); };

  const addSlab = () => setForm({ ...form, discount_slabs: [...form.discount_slabs, { min_qty: 100, discount_pct: 5 }] });
  const removeSlab = (i) => setForm({ ...form, discount_slabs: form.discount_slabs.filter((_, j) => j !== i) });

  return (
    <div className="space-y-3" data-testid="contracts-tab">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-contract-btn"><Plus className="w-3.5 h-3.5" /> Contract</Button></div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Contract#</th><th>Vendor</th><th>Item</th><th className="text-right">Price</th><th className="text-right">MOQ</th><th className="text-right">Lead</th><th>Effective</th><th>Slabs</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No contracts yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`contract-row-${r.contract_number}`}>
                <td className="font-mono text-xs">{r.contract_number}</td>
                <td>{r.vendor_name}</td>
                <td>{r.item_name}<div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div></td>
                <td className="text-right tabular-nums">{fmtINR(r.contract_price)}</td>
                <td className="text-right tabular-nums text-xs">{fmtNum(r.moq)}</td>
                <td className="text-right tabular-nums text-xs">{r.lead_time_days}d</td>
                <td className="text-xs">{fmtDate(r.effective_from)}{r.effective_to && ` → ${fmtDate(r.effective_to)}`}</td>
                <td className="text-xs">{(r.discount_slabs || []).length}</td>
                <td className="text-right"><Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New Vendor Contract</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="contract-vendor"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="contract-item"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Contract price ₹</Label><Input type="number" value={form.contract_price} onChange={(e) => setForm({ ...form, contract_price: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" data-testid="contract-price" /></div>
            <div><Label>MOQ</Label><Input type="number" value={form.moq} onChange={(e) => setForm({ ...form, moq: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Lead time (days)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Payment terms</Label><Input value={form.payment_terms || ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Effective from</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Effective to</Label><Input type="date" value={form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <div className="border rounded-sm">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Volume discount slabs</div>
              <Button variant="ghost" size="sm" onClick={addSlab} className="rounded-sm gap-1 h-7"><Plus className="w-3 h-3" /> Slab</Button>
            </div>
            {form.discount_slabs.map((s, i) => (
              <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center border-b border-border/40">
                <span className="col-span-2 text-xs">Min qty ≥</span>
                <Input type="number" value={s.min_qty} onChange={(e) => setForm({ ...form, discount_slabs: form.discount_slabs.map((x, j) => j === i ? { ...x, min_qty: Number(e.target.value) } : x) })} className="col-span-3 rounded-sm h-8 tabular-nums text-right text-xs" />
                <span className="col-span-2 text-xs text-center">Discount %</span>
                <Input type="number" value={s.discount_pct} onChange={(e) => setForm({ ...form, discount_slabs: form.discount_slabs.map((x, j) => j === i ? { ...x, discount_pct: Number(e.target.value) } : x) })} className="col-span-3 rounded-sm h-8 tabular-nums text-right text-xs" />
                <button className="col-span-2" onClick={() => removeSlab(i)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
              </div>
            ))}
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="contract-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// -------- PRs --------
function PRTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [prView, setPrView] = useState(null);
  const emptyForm = { date: today(), branch_id: "", department: "", priority: "normal", remarks: "",
                       lines: [{ item_id: "", qty: 1, uom: "PCS", estimated_price: 0, vendor_id: "", vendor_name: "", vendor_grade: "" }] };
  const [form, setForm] = useState(emptyForm);
  const [chainPreview, setChainPreview] = useState({ count: 0, chain: [] });

  const load = async () => {
    const params = branchFilter && branchFilter !== "all" ? { branch_id: branchFilter } : {};
    setRows((await api.get("/procurement/prs", { params })).data);
    setItems((await api.get("/items")).data);
    setBranches((await api.get("/branches")).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter]);

  // Total estimated computed live from lines
  const totalEst = useMemo(
    () => form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.estimated_price || 0), 0),
    [form.lines]
  );

  // Debounced fetch of the approval chain preview whenever total changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/procurement/approver-chain", {
          params: { doc_type: "purchase_requisition", amount: totalEst },
        });
        if (!cancelled) setChainPreview(r.data);
      } catch (e) {
        if (!cancelled) setChainPreview({ count: 0, chain: [] });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [totalEst, open]);

  // Auto-fill vendor + price when item is picked
  const applyPreferred = async (i, item_id) => {
    if (!item_id) return;
    try {
      const r = await api.get("/procurement/preferred-vendor", { params: { item_id } });
      const pv = r.data;
      setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? {
        ...x, item_id, vendor_id: pv.vendor_id, vendor_name: pv.vendor_name,
        vendor_grade: pv.vendor_grade || "", estimated_price: pv.contract_price,
      } : x) }));
      toast.success(`Preferred vendor: ${pv.vendor_name} (grade ${pv.vendor_grade || "—"}) @ ₹${pv.contract_price}`);
    } catch (e) {
      // Not fatal — just leave vendor blank
      setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, item_id } : x) }));
    }
  };

  const save = async () => {
    if (!form.branch_id) return toast.error("Branch is required");
    const lines = form.lines.filter((l) => l.item_id && Number(l.qty) > 0).map((l) => ({
      item_id: l.item_id, qty: l.qty, uom: l.uom, estimated_price: l.estimated_price,
      vendor_id: l.vendor_id || null,
    }));
    if (lines.length === 0) return toast.error("Add at least one line");
    try { await api.post("/procurement/prs", { ...form, lines }); toast.success("PR created"); setOpen(false); setForm(emptyForm); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const action = async (pr, act) => {
    try {
      const r = await api.post(`/procurement/prs/${pr.id}/${act}`);
      if (act === "convert-to-po") {
        const nums = (r.data.purchase_orders || []).map((p) => p.po_number).join(", ");
        toast.success(`Converted → ${r.data.count} PO(s): ${nums}`);
      } else {
        toast.success(act);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div className="space-y-3" data-testid="prs-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Branch:</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="rounded-sm h-9 text-xs w-48" data-testid="pr-branch-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-pr-btn"><Plus className="w-3.5 h-3.5" /> Purchase Requisition</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>PR#</th><th>Date</th><th>Branch</th><th>Dept</th><th className="text-right">Lines</th><th className="text-right">Est ₹</th><th>Priority</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No PRs yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => setPrView(r)} className="cursor-pointer hover:bg-muted/40" data-testid={`pr-row-${r.pr_number}`}>
                <td className="font-mono text-xs">{r.pr_number}</td>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td className="text-xs">{branchName(r.branch_id)}</td>
                <td className="text-xs">{r.department || "—"}</td>
                <td className="text-right tabular-nums text-xs">{r.lines?.length || 0}</td>
                <td className="text-right tabular-nums">{fmtINR(r.total_estimated)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${r.priority === "urgent" ? "text-red-500" : r.priority === "high" ? "text-orange-500" : "text-muted-foreground"}`}>{r.priority}</span></td>
                <td>
                  <span className={`text-[10px] font-mono uppercase ${r.status === "approved" ? "text-emerald-600" : r.status === "rejected" ? "text-red-500" : r.status === "submitted" ? "text-yellow-500" : "text-muted-foreground"}`}>{r.status}</span>
                  {r.status === "submitted" && r.approval_chain_size > 0 && (
                    <span className="text-[9px] font-mono text-muted-foreground ml-1">L{r.current_approval_level}/{r.approval_chain_size}</span>
                  )}
                </td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {r.status === "draft" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => action(r, "submit")} data-testid={`submit-pr-${r.pr_number}`}><Send className="w-3 h-3" /> Submit</Button>}
                  {r.status === "submitted" && <>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-600" onClick={() => action(r, "approve")} data-testid={`approve-pr-${r.pr_number}`}><CheckCircle2 className="w-3 h-3" /> Approve</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => action(r, "reject")}><XCircle className="w-3 h-3" /> Reject</Button>
                  </>}
                  {r.status === "approved" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => action(r, "convert-to-po")} data-testid={`convert-pr-${r.pr_number}`}><Zap className="w-3 h-3" /> Convert to PO</Button>}
                  {r.status === "converted" && <span className="text-[10px] font-mono uppercase text-emerald-700">✓ converted</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!prView} onOpenChange={(o) => !o && setPrView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto space-y-4" data-testid="pr-view-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase text-accent px-2 py-0.5 rounded-sm bg-accent/10">PR</span>
              {prView?.pr_number}
              <span className={`text-[10px] font-mono uppercase ml-auto ${prView?.status === "approved" ? "text-emerald-600" : prView?.status === "rejected" ? "text-red-500" : "text-muted-foreground"}`}>{prView?.status}</span>
            </SheetTitle>
          </SheetHeader>
          {prView && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Date</div><div className="mt-1 font-mono">{fmtDate(prView.date)}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Branch</div><div className="mt-1">{branchName(prView.branch_id)}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Priority</div><div className={`mt-1 font-mono uppercase ${prView.priority === "urgent" ? "text-red-500" : prView.priority === "high" ? "text-orange-500" : ""}`}>{prView.priority}</div></div>
                <div className="card-sharp p-3 col-span-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Department / Reason</div><div className="mt-1">{prView.department || "—"} · {prView.reason_name || prView.reason || "—"}</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total est.</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(prView.total_estimated || 0)}</div></div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Line items ({prView.lines?.length || 0})</div>
                <div className="border rounded-sm overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">SKU / Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Est ₹</th><th className="p-2 text-right">Value ₹</th></tr></thead>
                    <tbody>
                      {(prView.lines || []).map((l, i) => {
                        const it = items.find((x) => x.id === l.item_id);
                        return (
                          <tr key={i} className="border-t">
                            <td className="p-2"><div className="font-mono text-[10px] text-muted-foreground">{it?.sku || l.sku || "—"}</div><div>{it?.name || l.description}</div></td>
                            <td className="p-2 text-right tabular-nums">{l.qty} {l.uom || ""}</td>
                            <td className="p-2 text-right tabular-nums">{fmtINR(l.estimated_price || 0)}</td>
                            <td className="p-2 text-right tabular-nums font-medium">{fmtINR((l.qty || 0) * (l.estimated_price || 0))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {prView.linked_po_ids?.length > 0 && (
                <div className="border rounded-sm p-3 bg-emerald-500/5">
                  <div className="text-[10px] font-mono uppercase text-emerald-600">// Converted to PO(s)</div>
                  <div className="mt-1 text-xs font-mono">{prView.linked_po_ids.join(", ")}</div>
                </div>
              )}
              {prView.approvals?.length > 0 && (
                <div className="border rounded-sm p-3 bg-muted/20">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Approval trail</div>
                  <div className="space-y-1.5">
                    {prView.approvals.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 border rounded-sm">L{a.level}</span>
                        <span className={`font-mono uppercase text-[10px] ${a.action === "approved" ? "text-emerald-600" : a.action === "rejected" ? "text-red-500" : "text-muted-foreground"}`}>{a.action}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="font-mono text-[10px]">{a.date}</span>
                        {a.remarks && <span className="italic text-muted-foreground">"{a.remarks}"</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New Purchase Requisition</SheetTitle><SheetDescription className="sr-only">Create a purchase requisition with branch, department, priority and line items. Approval chain preview updates live based on total.</SheetDescription></SheetHeader>
          <div className="grid grid-cols-4 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div>
              <Label>Branch <span className="text-red-500">*</span></Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="pr-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>
                  {branches.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No branches configured</div>}
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" data-testid="pr-dept" /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="border rounded-sm">
            <div className="px-3 py-2 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Lines</div>
            <table className="w-full dense">
              <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Est ₹</th><th className="text-right">Line ₹</th><th></th></tr></thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} data-testid={`pr-line-${i}`}>
                    <td className="p-1">
                      <Select value={l.item_id} onValueChange={(v) => applyPreferred(i, v)}>
                        <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Item" /></SelectTrigger>
                        <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</div></SelectContent>
                      </Select>
                      {l.vendor_name && <div className="text-[10px] font-mono text-emerald-600 mt-0.5" data-testid={`pr-preferred-${i}`}>→ {l.vendor_name} {l.vendor_grade && `· grade ${l.vendor_grade}`}</div>}
                    </td>
                    <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums text-xs w-20" /></td>
                    <td className="p-1"><Input type="number" value={l.estimated_price} onChange={(e) => setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, estimated_price: Number(e.target.value) } : x) }))} className="rounded-sm h-8 text-right tabular-nums text-xs w-24" /></td>
                    <td className="text-right tabular-nums text-xs">{fmtINR(l.qty * l.estimated_price)}</td>
                    <td className="text-center">{form.lines.length > 1 && <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-2"><Button variant="ghost" size="sm" onClick={() => setForm({ ...form, lines: [...form.lines, { item_id: "", qty: 1, uom: "PCS", estimated_price: 0, vendor_id: "", vendor_name: "", vendor_grade: "" }] })} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Line</Button></div>
          </div>

          {/* Chain preview */}
          <div className="border rounded-sm p-3 bg-muted/30" data-testid="pr-chain-preview">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
              Approval chain · {fmtINR(totalEst)}
            </div>
            {chainPreview.count === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                No matching approval rule for this amount — PR will submit without approvers.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="pr-chain-list">
                <span className="text-[10px] font-mono text-muted-foreground">You</span>
                {chainPreview.chain.map((c, i) => (
                  <React.Fragment key={i}>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 border rounded-sm bg-background" data-testid={`chain-step-${i + 1}`}>
                      L{i + 1} · {c.approver_role}
                    </span>
                  </React.Fragment>
                ))}
                <ArrowRight className="w-3 h-3 text-emerald-600" />
                <span className="text-[10px] font-mono uppercase text-emerald-600">approved</span>
              </div>
            )}
          </div>

          <div className="text-right text-sm">Total est: <span className="font-medium tabular-nums" data-testid="pr-total-est">{fmtINR(totalEst)}</span></div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="pr-save">Save draft</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// -------- Approval Matrix --------
function ApprovalMatrixTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ doc_type: "purchase_requisition", min_amount: 0, max_amount: 100000, approver_role: "manager", sequence: 1, is_active: true });
  const load = async () => setRows((await api.get("/procurement/approval-matrix")).data);
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/procurement/approval-matrix", form); toast.success("Rule added"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete rule?")) return; await api.delete(`/procurement/approval-matrix/${id}`); load(); };
  return (
    <div className="space-y-3" data-testid="approval-matrix-tab">
      <div className="card-sharp p-4 grid grid-cols-6 gap-3 items-end">
        <div><Label className="text-xs">Doc type</Label>
          <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
            <SelectTrigger className="rounded-sm mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["purchase_requisition", "purchase_order", "invoice"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Min ₹</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: Number(e.target.value) })} className="rounded-sm mt-1 h-9 tabular-nums text-right" /></div>
        <div><Label className="text-xs">Max ₹</Label><Input type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: Number(e.target.value) })} className="rounded-sm mt-1 h-9 tabular-nums text-right" /></div>
        <div><Label className="text-xs">Approver role</Label><Input value={form.approver_role} onChange={(e) => setForm({ ...form, approver_role: e.target.value })} className="rounded-sm mt-1 h-9" data-testid="approval-role" /></div>
        <div><Label className="text-xs">Seq</Label><Input type="number" value={form.sequence} onChange={(e) => setForm({ ...form, sequence: Number(e.target.value) })} className="rounded-sm mt-1 h-9 tabular-nums text-right" /></div>
        <Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white h-9" data-testid="approval-add">Add rule</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Seq</th><th>Doc type</th><th className="text-right">Min ₹</th><th className="text-right">Max ₹</th><th>Approver role</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No rules yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="text-xs">{r.sequence}</td>
                <td className="text-xs font-mono">{r.doc_type}</td>
                <td className="text-right tabular-nums">{fmtINR(r.min_amount)}</td>
                <td className="text-right tabular-nums">{fmtINR(r.max_amount)}</td>
                <td className="text-xs">{r.approver_role}</td>
                <td className="text-right"><Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------- Vendor Scorecard --------
function ScorecardTab() {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 180); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(today());
  const load = async () => setRows((await api.get("/procurement/vendor-scorecards", { params: { from_date: from, to_date: to } })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const gradeColor = { A: "text-emerald-600", B: "text-yellow-500", C: "text-orange-500", D: "text-red-500" };
  return (
    <div className="space-y-3" data-testid="scorecard-tab">
      <div className="flex items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <Button onClick={load} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="scorecard-run">Refresh</Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Vendor</th><th className="text-right">GRNs</th><th className="text-right">Value</th><th className="text-right">OTD %</th><th className="text-right">QC pass %</th><th className="text-right">Avg lead d</th><th className="text-right">PPV ₹</th><th className="text-center">Grade</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No vendor activity in this window.</td></tr>}
            {rows.map((r) => (
              <tr key={r.vendor_id} data-testid={`sc-${r.vendor_id}`}>
                <td>{r.vendor_name}</td>
                <td className="text-right tabular-nums text-xs">{r.total_grns}</td>
                <td className="text-right tabular-nums">{fmtINR(r.total_value)}</td>
                <td className="text-right tabular-nums text-xs">{r.on_time_delivery_pct ?? "—"}</td>
                <td className="text-right tabular-nums text-xs">{r.qc_pass_pct ?? "—"}</td>
                <td className="text-right tabular-nums text-xs">{r.avg_lead_time_days ?? "—"}</td>
                <td className={`text-right tabular-nums text-xs ${r.purchase_price_variance > 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtINR(r.purchase_price_variance)}</td>
                <td className={`text-center font-medium ${gradeColor[r.grade]}`}><Award className="w-4 h-4 inline" /> {r.grade}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------- ABC/XYZ --------
function ABCXYZTab() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 180); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(today());
  const load = async () => setData((await api.get("/procurement/abc-xyz", { params: { from_date: from, to_date: to } })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const CLASSES = [
    ["AX", "text-emerald-700", "High value + steady"],
    ["AY", "text-emerald-600", "High value + variable"],
    ["AZ", "text-yellow-600", "High value + erratic"],
    ["BX", "text-blue-600", "Medium + steady"],
    ["BY", "text-blue-500", "Medium + variable"],
    ["BZ", "text-orange-500", "Medium + erratic"],
    ["CX", "text-muted-foreground", "Low + steady"],
    ["CY", "text-muted-foreground", "Low + variable"],
    ["CZ", "text-red-500", "Low + erratic"],
  ];
  return (
    <div className="space-y-3" data-testid="abcxyz-tab">
      <div className="flex items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <Button onClick={load} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="abcxyz-run">Analyse</Button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-9 gap-2">
            {CLASSES.map(([c, color, hint]) => (
              <div key={c} className="card-sharp p-2 text-center">
                <div className={`font-mono text-xs uppercase ${color}`}>{c}</div>
                <div className="text-2xl font-medium tabular-nums">{data.summary.counts[c] || 0}</div>
                <div className="text-[9px] text-muted-foreground mt-1">{hint}</div>
              </div>
            ))}
          </div>
          <div className="card-sharp overflow-hidden max-h-[60vh] overflow-y-auto">
            <table className="w-full dense">
              <thead className="sticky top-0 bg-card"><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Value</th><th className="text-right">Cum %</th><th className="text-center">ABC</th><th className="text-center">XYZ</th><th>Class</th></tr></thead>
              <tbody>
                {data.items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No outward stock movements found — MRP relies on this to rank items.</td></tr>}
                {data.items.map((r) => (
                  <tr key={r.item_id}>
                    <td>{r.name}<div className="text-[10px] font-mono text-muted-foreground">{r.sku}</div></td>
                    <td className="text-right tabular-nums text-xs">{fmtNum(r.qty)}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.value)}</td>
                    <td className="text-right tabular-nums text-xs">{r.cum_pct}%</td>
                    <td className="text-center font-mono">{r.abc}</td>
                    <td className="text-center font-mono">{r.xyz}</td>
                    <td className="font-mono uppercase text-[10px] text-accent">{r.classification}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Procurement() {
  const { hasModule } = useSubscription();
  if (!hasModule("procurement")) return <div className="p-8"><div className="card-sharp p-10 text-center"><div className="font-heading text-2xl">Procurement E2E requires Enterprise Manufacturing</div><a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a></div></div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="procurement-page">
      <PageHeader eyebrow="Procurement" title="Procurement E2E"
        description="Contract prices with volume slabs · Purchase Requisitions with N-level approval matrix · Vendor scorecards (OTD, QC, PPV) · ABC/XYZ analytics." />
      <Tabs defaultValue="inbox">
        <TabsList className="rounded-sm">
          <TabsTrigger value="inbox" className="rounded-sm" data-testid="tab-inbox">Approval Inbox</TabsTrigger>
          <TabsTrigger value="contracts" className="rounded-sm" data-testid="tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="prs" className="rounded-sm" data-testid="tab-prs">Purchase Requisitions</TabsTrigger>
          <TabsTrigger value="matrix" className="rounded-sm" data-testid="tab-matrix">Approval Matrix</TabsTrigger>
          <TabsTrigger value="scorecard" className="rounded-sm" data-testid="tab-scorecard">Vendor Scorecard</TabsTrigger>
          <TabsTrigger value="abcxyz" className="rounded-sm" data-testid="tab-abcxyz">ABC / XYZ</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-4"><ApprovalInboxCard /></TabsContent>
        <TabsContent value="contracts" className="mt-4"><ContractsTab /></TabsContent>
        <TabsContent value="prs" className="mt-4"><PRTab /></TabsContent>
        <TabsContent value="matrix" className="mt-4"><ApprovalMatrixTab /></TabsContent>
        <TabsContent value="scorecard" className="mt-4"><ScorecardTab /></TabsContent>
        <TabsContent value="abcxyz" className="mt-4"><ABCXYZTab /></TabsContent>
      </Tabs>
    </div>
  );
}
